import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { SurveyMeasurementStatuses } from '@core/enums/survey-measurement-statuses';
import { MeasurementHelper } from '@core/helpers/measurement.helper';
import { SurveyFileHelper } from '@core/helpers/survey-file.helper';
import { AlarmConfig } from '@core/interfaces/alarm-config';
import {
  convertToCorrosionMonitoringLocation,
  convertToSurveyPoint,
  CorrosionMonitoringLocation,
} from '@core/interfaces/corrosion-monitoring-location';
import { SurveyPoint } from '@core/interfaces/survey-point';
import { UrlService } from '@core/services/url.service';
import { Observable, map } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CorrosionMonitoringLocationsService {
  readonly #httpClient = inject(HttpClient);
  readonly #urlService = inject(UrlService);

  public updateCMLs(fileId: string, points: Exclude<SurveyPoint, 'isCustom'>[], taskId: string): Observable<SurveyPoint[]> {
    const url = this.#urlService.getUrl(`tasks/${taskId}/corrosion-monitoring-locations`);
    const cmls = points.map(point => ({ ...point, fileId }));
    return this.#httpClient.put<SurveyPoint[]>(url, cmls);
  }

  public updateCML(fileId: string, point: SurveyPoint, taskId: string, alarmConfig: AlarmConfig): Observable<SurveyPoint> {
    const normalized = SurveyFileHelper.setMeasurementToZeroForNullValue(point);
    MeasurementHelper.setMeasurementStatus(normalized, alarmConfig);
    const corrosionMonitoringLocation = convertToCorrosionMonitoringLocation(normalized, fileId);
    const url = this.#urlService.getUrl(`tasks/${taskId}/corrosion-monitoring-locations/${corrosionMonitoringLocation.id}`);
    return this.#httpClient
      .put<CorrosionMonitoringLocation>(url, corrosionMonitoringLocation)
      .pipe(map(updated => convertToSurveyPoint(updated)));
  }

  public addCML(fileId: string, cml: SurveyPoint, taskId: string): Observable<SurveyPoint> {
    const url = this.#urlService.getUrl(`tasks/${taskId}/corrosion-monitoring-locations`);
    return this.#httpClient.post<SurveyPoint>(url, { ...cml, fileId });
  }

  public deleteCML(cmlId: string, taskId: string): Observable<void> {
    const url = this.#urlService.getUrl(`tasks/${taskId}/corrosion-monitoring-locations/${cmlId}`);
    return this.#httpClient.delete<void>(url);
  }

  public fetchUnassignedCMLs(taskId: string): Observable<SurveyPoint[]> {
    const url = this.#urlService.getUrl(`tasks/${taskId}/corrosion-monitoring-locations`);
    return this.#httpClient
      .get<CorrosionMonitoringLocation[]>(url, {
        params: {
          status: SurveyMeasurementStatuses.UnAssigned,
          isCustom: false,
        },
      })
      .pipe(map(cmls => cmls.map(convertToSurveyPoint)));
  }

  public editMeasurementComment(
    request: { cmlId: string; measurementId: string; comment: string; status?: string },
    taskId: string,
  ): Observable<void> {
    const url = this.#urlService.getUrl(
      `tasks/${taskId}/corrosion-monitoring-locations/${request.cmlId}/measurements/${request.measurementId}/comment`,
    );
    return this.#httpClient.put<void>(url, { comment: request.comment, status: request.status });
  }
}
